//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import Check from 'assets/check.svg';
import ExclamationOutline from 'assets/exclamation-outline.svg';
import ErrorCircle from 'assets/exclamation-x-circle.svg';
import Remove from 'assets/remove.svg';
import { AxiosResponse } from 'axios';
import { useAuth } from 'contexts/AuthProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { useModal } from 'contexts/ModalProvider';
import { useToast } from 'contexts/ToastProvider';
import { AbortDiagnoseDto } from 'dto/abort-diagnose.dto';
import { DiagnoseDto } from 'dto/diagnose.dto';
import { UpdateDiagnoseTimeoutDto } from 'dto/update-diagnose-timeout.dto';
import { useTimer } from 'hook/useTimer';
import { DiagnosticResultQuery } from 'models/diagnostic-result-query';
import { OptionType } from 'models/option-type';
import { DiagnosticErrorStatus, DiagnosticResultItemError, ResponseDiagnosticResult } from 'models/resposne-diagnostic-result';
import { DiagnosticType } from 'pages/Diagnostic';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isBitmapMatch } from 'utils/helper';
import Validator from 'utils/validator';
import SingleSelect from './SingleSelect';

type FormValues = {
  device: string;
};

type CheckItem = {
  key: string;
  name: string;
  status: DiagnosticErrorStatus;
  svg: 'toastIcon-warning' | 'toastIcon-success' | 'toastIcon-error' | 'toastIcon-info' | 'cyfi-svg';
  icon?: string;
  bgColor: string;
  text: string;
  errors: {
    i18nDiagnosticKey: string;
    params?: string[];
  }[];
};

type Progress = 'init' | 'started' | 'finished';

enum DiagnosticItemStatusColor {
  Success = 'toastIcon-success',
  Failed = 'toastIcon-error',
  NotComplete = 'toastIcon-info',
  NotSupport = 'toastIcon-warning',
}

interface DiagnosticDiaglogProps {
  diagnosticType: DiagnosticType;
  readonly devicesOptions: OptionType[];
}

const DIAGNOSTIC_STATUS_CONFIG: Record<string, Partial<CheckItem>> = {
  success: {
    svg: DiagnosticItemStatusColor.Success,
    icon: Check,
    bgColor: 'bg-cyfi-success',
    text: 'text-toast-success-800',
  },
  failed: {
    svg: DiagnosticItemStatusColor.Failed,
    icon: Remove,
    bgColor: 'bg-cyfi-error',
    text: 'text-toast-error-800',
  },
  notSupport: {
    svg: DiagnosticItemStatusColor.NotSupport,
    icon: ExclamationOutline,
    bgColor: 'bg-cyfi-warning',
    text: 'text-toast-warning-800',
  },
  notComplete: {
    svg: DiagnosticItemStatusColor.NotComplete,
    bgColor: 'bg-cyfi-info',
    text: 'text-toast-info-800',
  },
} as const;

/**
 * 根據錯誤碼取得狀態配置
 * @param errCode - 錯誤碼
 * @returns 狀態配置
 */
const getStatusConfig = (errCode: number): Partial<CheckItem> => {
  // 處理無效的錯誤碼
  if (!Number.isInteger(errCode) || errCode < 0 || errCode > 0xffff) {
    return DIAGNOSTIC_STATUS_CONFIG.failed;
  }

  const highByte = (errCode >> 8) & 0xff;
  const lowByte = errCode & 0xff;

  // Check reserve bits (0x10, 0x02, 0x01)
  if ((highByte & 0x13) !== 0) {
    return DIAGNOSTIC_STATUS_CONFIG.failed;
  }

  const isSuccess: boolean = isBitmapMatch(highByte, 0x80);
  const isNormal: boolean = isBitmapMatch(highByte, 0x40);
  const isFail: boolean = isBitmapMatch(highByte, 0x20);
  const isCommon: boolean = isBitmapMatch(highByte, 0x08);
  const isPrivate: boolean = isBitmapMatch(highByte, 0x04);

  const typeCount = Number(isSuccess) + Number(isNormal) + Number(isFail);

  // Not Complete: 0x0001
  if (typeCount === 0 && highByte === 0 && lowByte === 1) {
    return DIAGNOSTIC_STATUS_CONFIG.notComplete;
  }

  // 無效的類型組合
  if (typeCount !== 1) {
    return DIAGNOSTIC_STATUS_CONFIG.failed;
  }

  // Success: 0x8400
  if (isSuccess) {
    if (isPrivate && !isCommon && lowByte === 0) {
      return DIAGNOSTIC_STATUS_CONFIG.success;
    }
    return DIAGNOSTIC_STATUS_CONFIG.failed;
  }

  // Not Support: 0x4800, 0x4400
  if (isNormal) {
    // 0x4800: common (0x08) + normal (0x40)
    if (isCommon && !isPrivate && lowByte === 0) {
      return DIAGNOSTIC_STATUS_CONFIG.notSupport;
    }
    // 0x4400: private (0x04) + normal (0x40)
    if (isPrivate && !isCommon) {
      return DIAGNOSTIC_STATUS_CONFIG.notSupport;
    }
    return DIAGNOSTIC_STATUS_CONFIG.failed;
  }

  // Failed: 0x2xxx
  if (isFail) {
    if (isCommon === isPrivate) {
      return DIAGNOSTIC_STATUS_CONFIG.failed;
    }
    // 所有失敗狀態都返回 failed 配置
    // 包括: private fail (0x2400), timeout (0x2800), outputError (0x2801), fileNotFound (0x2802)
    return DIAGNOSTIC_STATUS_CONFIG.failed;
  }

  // Default
  return DIAGNOSTIC_STATUS_CONFIG.failed;
};

/**
 * NOTE: 當 STP diable 且發生 Loop，API 完全無法返回結果，需要設定 timeout 強制結束
 */
const withTimeout = (promise: Promise<any>, timeoutMs: number, errorMessage: string = 'timeout'): Promise<any> => {
  return Promise.race([promise, new Promise<never>((_, reject) => setTimeout(() => reject(new Error(errorMessage)), timeoutMs))]);
};

/**
 * 檢查是否為超時錯誤
 */
const isTimeoutError = (error: any, timeoutMessage: string): boolean => {
  return error instanceof Error && error.message === timeoutMessage;
};

const DiagnosticDialog = forwardRef(({ diagnosticType, devicesOptions }: DiagnosticDiaglogProps, ref) => {
  const { t } = useTranslation();
  const { diagnosticItems } = useConfig();
  const { closeCustomModal } = useModal();
  const { addToast } = useToast();
  const { currentLng, getCbtDiagnoseResult, postPingDevice, postCbtDiagnostic, postAbortCbtDiagnostic, putCbtDiagnosticTimeout } = useAuth();
  const {
    handleSubmit,
    getValues,
    control,
    formState: { errors },
  } = useForm<FormValues>();
  const [checks, setChecks] = useState<CheckItem[]>([]);
  const [progress, setProgress] = useState<Progress>('init');
  const [cancelDiagnose, setCancelDiagnose] = useState<boolean>(false);
  const serialNumberRef = useRef<string>('');
  const commandUuidRef = useRef<string>('');
  const cbtDiagnosticRegularTimesRef = useRef<number>(5);
  const wifiBandList = [
    { value: '2G', label: t('settings.profileManagement.ssidsEditForm24GHz') },
    { value: '5G', label: t('settings.profileManagement.ssidsEditForm5GHz') },
    { value: '6G', label: t('settings.profileManagement.ssidsEditForm6GHz') },
  ];
  const completedChecks = useMemo(() => checks.filter((c) => c.status !== DiagnosticErrorStatus.NotComplete), [checks]);
  const incompleteChecks = useMemo(() => checks.filter((c) => c.status === DiagnosticErrorStatus.NotComplete), [checks]);
  const PROMISE_TIMOUT = 15 * 1000; // 15 秒
  const TIMEOUT_DURATION = 45 * 1000; // 45 秒
  const CHECK_INTERVAL = cbtDiagnosticRegularTimesRef.current * 1000;
  const statusPoller = useTimer<AxiosResponse<ResponseDiagnosticResult>>({
    checkInterval: CHECK_INTERVAL,
    timeoutDuration: TIMEOUT_DURATION,
    timeoutMode: 'fixed', // 固定時長，不管有沒有進度
    onCheck: async () => getDiagnosticResult(),
    shouldContinue: (status) => status.data.errCode === 0xf001,
    isSuccess: (status) => status.data.errCode === 0,
    isError: (status) => status.data.errCode === 1,
    onSuccess: (status) => {
      updateChecks(status.data);
      setProgress('finished');
    },
    onError: (status) => {
      updateChecks(status.data);
      setProgress('finished');
    },
    onTimeout: () => updateDiagnoseTimeout(),
    onProgress: (status) => updateChecks(status.data),
    hasUpdated: (newData, oldData) => {
      if (!oldData) return true;
      return JSON.stringify(newData.data.results) !== JSON.stringify(oldData.data.results);
    },
  });

  useEffect(() => {
    initialCheckItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosticType]);

  useImperativeHandle(ref, () => ({
    onCloseDiagnosticDialog,
  }));

  const getDiagnosticResult = () => {
    const diagnosticResultQuery: DiagnosticResultQuery = { type: diagnosticType === DiagnosticType.SYSTEM ? DiagnosticType.SYSTEM : 'device' };
    if (diagnosticType !== DiagnosticType.SYSTEM) {
      Object.assign(diagnosticResultQuery, { serialNumber: serialNumberRef.current, commandUuid: commandUuidRef.current });
    }
    return getCbtDiagnoseResult(diagnosticResultQuery);
  };

  const updateDiagnoseTimeout = () => {
    const updateDiagnoseDto: UpdateDiagnoseTimeoutDto = { serialNumber: serialNumberRef.current, commandUuid: commandUuidRef.current };
    putCbtDiagnosticTimeout(updateDiagnoseDto).then((response) => {
      setProgress('finished');
      updateChecks(response.data);
    });
  };

  const initialCheckItems = () => {
    const checkItems: CheckItem[] = [];
    if (diagnosticType !== DiagnosticType.SYSTEM) {
      checkItems.push({
        key: 'serviceDeviceConnectivity',
        name: t(`diagnostic.diagnosticItem.serviceDeviceConnectivity`),
        status: DiagnosticErrorStatus.NotComplete,
        svg: DiagnosticItemStatusColor.NotComplete,
        bgColor: 'bg-cyfi-info',
        text: 'text-toast-info-800',
        errors: [],
      });
    }
    checkItems.push(
      ...diagnosticItems[diagnosticType].map((diagnosticItem) => ({
        key: diagnosticItem,
        name: t(`diagnostic.diagnosticItem.${diagnosticItem}`),
        status: DiagnosticErrorStatus.NotComplete,
        svg: DiagnosticItemStatusColor.NotComplete,
        bgColor: 'bg-cyfi-info',
        text: 'text-toast-info-800',
        errors: [],
      }))
    );
    setChecks(checkItems);
  };

  const updateCheckItemStatus = (resultName: string, error: DiagnosticResultItemError) => {
    const statusConfig =
      error.i18nDiagnosticKey === 'diagnostic.messages.common.internalError' ? DIAGNOSTIC_STATUS_CONFIG.failed : getStatusConfig(error.errCode);

    setChecks((prev) =>
      prev.map((check) => {
        if (check.key !== resultName) return check;

        const newError = {
          i18nDiagnosticKey: error.i18nDiagnosticKey,
          params: error.params,
        };

        const filteredErrors = check.errors.filter((e) => e.i18nDiagnosticKey !== newError.i18nDiagnosticKey);

        return {
          ...check,
          status: error.errCode,
          ...statusConfig,
          errors: [...filteredErrors, newError],
        };
      })
    );
  };

  const updateChecks = useCallback((data: ResponseDiagnosticResult) => {
    if (!Object.keys(data).length) return;

    const { results } = data;
    if (results.length === 0) return;

    results.forEach((result) => {
      result.errors.forEach((error) => {
        updateCheckItemStatus(result.name, error);
      });
    });
  }, []);

  const onCancelDiagnose = async () => {
    if (cancelDiagnose) return;

    setCancelDiagnose(true);
    statusPoller.stopTimer();
    const abortDiagnosticDto: AbortDiagnoseDto = { type: diagnosticType === DiagnosticType.SYSTEM ? DiagnosticType.SYSTEM : 'device' };
    if (diagnosticType !== DiagnosticType.SYSTEM) {
      Object.assign(abortDiagnosticDto, { serialNumber: serialNumberRef.current, commandUuid: commandUuidRef.current });
    }
    return postAbortCbtDiagnostic(abortDiagnosticDto)
      .then((response) => {
        updateChecks(response.data);
      })
      .catch((error) => {
        console.error('Cancel diagnostic error:', error);
        addToast({
          message: error?.response?.data?.message ?? t('toast.somethingErrorOccurs'),
          color: 'bg-toast-error-50',
          icon: ErrorCircle,
          text: 'text-toast-error-800',
          svg: 'toastIcon-error',
        });
      })
      .finally(() => {
        setCancelDiagnose(false);
        setProgress('finished');
      });
  };

  const onCloseDiagnosticDialog = () => {
    try {
      if (progress === 'started') {
        onCancelDiagnose();
      }
    } catch (error) {
      console.error('Error during cancel:', error);
    } finally {
      closeCustomModal();
    }
  };

  const onSubmit = async (form: FormValues) => {
    initialCheckItems();
    setProgress('started');
    serialNumberRef.current = form.device;
    const isSystemDiagnose = diagnosticType === DiagnosticType.SYSTEM;

    if (!isSystemDiagnose) {
      const pingTimeoutMessage = 'devicePingTimeout';
      try {
        // NOTE: 測試裝置 WebSocket 連線狀態（ping-pong）
        const pingDeviceResp = await withTimeout(postPingDevice(form.device, true), PROMISE_TIMOUT, pingTimeoutMessage);

        const { data } = pingDeviceResp;
        const result = data.results[0];
        const error = result.errors[0];
        updateCheckItemStatus(result.name, error);

        if (data.errCode === 1) {
          setProgress('finished');
          return;
        }
      } catch (error) {
        addToast({
          message: isTimeoutError(error, pingTimeoutMessage) ? t('toast.diagnoseTimeout') : t('toast.somethingErrorOccurs'),
          color: 'bg-toast-error-50',
          icon: ErrorCircle,
          text: 'text-toast-error-800',
          svg: 'toastIcon-error',
        });
        setProgress('finished');
        return;
      }
    }

    const diagnoseDto: DiagnoseDto = { type: diagnosticType === DiagnosticType.SYSTEM ? DiagnosticType.SYSTEM : 'device' };
    if (diagnosticType !== DiagnosticType.SYSTEM) {
      diagnoseDto.serialNumber = form.device;
    }

    const diagnosticTimeoutMessage = 'diagnosticTimeout';

    try {
      const response = await withTimeout(postCbtDiagnostic(diagnoseDto), TIMEOUT_DURATION, diagnosticTimeoutMessage);

      const { uuid, cbtDiagnosticRegularTimes } = response.data;
      commandUuidRef.current = uuid;
      cbtDiagnosticRegularTimesRef.current = cbtDiagnosticRegularTimes;

      statusPoller.startTimer();
    } catch (error) {
      const message = isTimeoutError(error, diagnosticTimeoutMessage)
        ? t('toast.diagnoseTimeout')
        : (error as any).response?.data?.message ?? t('toast.somethingErrorOccurs');

      if (message === 'diagnostic is not supported.') {
        for (const check of checks) {
          if (check.key === 'serviceDeviceConnectivity') continue;
          updateCheckItemStatus(check.key, {
            errCode: 0x4800,
            i18nDiagnosticKey: t('diagnostic.messages.common.notSupportDiagnostic'),
          });
        }
      } else {
        addToast({
          message,
          color: 'bg-toast-error-50',
          icon: ErrorCircle,
          text: 'text-toast-error-800',
          svg: 'toastIcon-error',
        });
      }
      statusPoller.stopTimer();
      setProgress('finished');
    }
  };

  const ReportDetail = ({ c }: { c: CheckItem }) => {
    const { t } = useTranslation();

    if (!c.errors.length) {
      return (
        <div className="collapse-content px-9 text-xs" data-testid={`diagnostic-dialog-report-detail-for-item-${c.key}`}>
          <></>
        </div>
      );
    }

    return (
      <div className="collapse-content px-9 text-xs flex flex-col">
        {c.errors.map(({ i18nDiagnosticKey, params }, errorIndex) => {
          // 未知 error 情況
          if (i18nDiagnosticKey === 'diagnostic.messages.common.somethingErrorOccurs') {
            // Storage Usage
            if (c.name === t('diagnostic.diagnosticItem.storageUsage') && params) {
              return (
                <div key={`${c.key}-${errorIndex}`} data-testid={`diagnostic-dialog-report-detail-for-item-${c.key}`}>
                  {t('toast.somethingErrorOccurs')}({params[0]})
                </div>
              );
            }
            return (
              <div key={`${c.key}-${errorIndex}`} data-testid={`diagnostic-dialog-report-detail-for-item-${c.key}`}>
                {t(i18nDiagnosticKey, { errorCode: `0x${c.status.toString(16).toUpperCase().padStart(4, '0')}` })}
              </div>
            );
          }

          // 沒有 params 的情況
          if (!params || params.length === 0) {
            return (
              <div key={`${c.key}-${errorIndex}`} data-testid={`diagnostic-dialog-report-detail-for-item-${c.key}`}>
                {t(i18nDiagnosticKey)}
              </div>
            );
          }

          // Channel Utilization
          if (c.name === t('diagnostic.diagnosticItem.channelUtilization')) {
            return params.map((param: string, index: number) => (
              <div key={`${c.key}-${errorIndex}-${index}`} data-testid={`diagnostic-dialog-report-detail-for-item-${c.key}`}>
                {t(i18nDiagnosticKey, {
                  channel: wifiBandList[index].label,
                  message: param !== '' ? `${param}%` : t('diagnostic.messages.eap.channelNotExist'),
                })}
              </div>
            ));
          }

          // Service Status
          if (c.name === t('diagnostic.diagnosticItem.serviceStatus')) {
            const isFailed = i18nDiagnosticKey.includes('failed');
            return params.map((param: string, index: number) => {
              const [serviceName, message] = param.split(',');
              const testId = `diagnostic-dialog-report-detail-for-item-${c.key}-${serviceName}`;

              const getStatusInfo = () => {
                if (!message) {
                  return {
                    i18nKey: 'diagnostic.messages.system.serviceStatus.success',
                    params: { currentValue: param },
                  };
                }

                const lowerMessage = message.toLowerCase();

                if (lowerMessage.includes('not enabled')) {
                  return {
                    i18nKey: 'diagnostic.messages.system.serviceStatus.skipped',
                    params: { currentValue: serviceName },
                  };
                }

                if (isFailed || lowerMessage.includes('fail')) {
                  return {
                    i18nKey: i18nDiagnosticKey,
                    params: { currentValue: serviceName, message },
                  };
                }

                return {
                  i18nKey: `diagnostic.messages.system.serviceStatus.${serviceName}Success`,
                  params: { currentValue: serviceName, message },
                };
              };

              const { i18nKey, params: statusParams } = getStatusInfo();

              return (
                <div key={`${c.key}-${errorIndex}-${index}`} data-testid={testId}>
                  {t(i18nKey, statusParams)}
                </div>
              );
            });
          }

          // Default Gateway
          if (c.name === t('diagnostic.diagnosticItem.defaultGateway')) {
            return (
              <div key={`${c.key}-${errorIndex}`}>
                <div>{t(i18nDiagnosticKey)}</div>
                {params.map((param: string, index: number) => (
                  <div key={`${c.key}-${errorIndex}-${index}`} data-testid={`diagnostic-dialog-report-detail-for-item-${c.key}`}>
                    * {param}
                  </div>
                ))}
              </div>
            );
          }

          // Portal Connectivity
          if (c.name === t('diagnostic.diagnosticItem.portalConnectivity') && c.status === DiagnosticErrorStatus.Failed) {
            return (
              <div key={`${c.key}-${errorIndex}`} data-testid={`diagnostic-dialog-report-detail-for-item-${c.key}`}>
                {t('toast.somethingErrorOccurs')}({params[0]})
              </div>
            );
          }

          // Database Integrity
          if (c.name === t('diagnostic.diagnosticItem.databaseIntegrity')) {
            return params.map((param: string, index: number) => {
              const testId = `diagnostic-dialog-report-detail-for-item-${c.key}-${index}`;
              if (param.includes(',')) {
                const [dbName, message] = param.split(',');
                return (
                  <div key={`${c.key}-${errorIndex}-${index}`} data-testid={testId}>
                    {t(i18nDiagnosticKey, { currentValue: dbName, message })}
                  </div>
                );
              }

              return (
                <div key={`${c.key}-${errorIndex}-${index}`} data-testid={testId}>
                  {t('diagnostic.messages.system.databaseIntegrity.success', { currentValue: param })}
                </div>
              );
            });
          }

          // 其他情況
          return params.map((param: string, index: number) => (
            <div key={`${c.key}-${errorIndex}-${index}`} data-testid={`diagnostic-dialog-report-detail-for-item-${c.key}`}>
              {t(i18nDiagnosticKey, { currentValue: param })}
            </div>
          ));
        })}
      </div>
    );
  };

  const Report = ({ c }: { c: CheckItem }) => (
    <div
      tabIndex={0}
      className={`collapse rounded-2xl px-4 ${c.bgColor} ${
        progress === 'finished' ? (c.status !== DiagnosticErrorStatus.NotComplete ? 'collapse-plus' : ' collapse-close') : ' collapse-close'
      }`}
      data-testid={`diagnostic-dialog-report-for-item-${c.key}`}
    >
      <div className={`collapse-title ${c.text}`}>
        {c.icon && <img className={`w-4 h-4 mr-2 ${c.svg}`} src={c.icon} alt={c.name} />}
        {c.name}
      </div>
      <ReportDetail c={c} />
    </div>
  );

  const CheckWithProgress = ({ c }: { c: CheckItem }) => (
    <div
      key={c.key}
      className="relative w-full bg-cyfi-info rounded-full h-8 overflow-hidden"
      data-testid={`diagnostic-dialog-check-with-progress-for-item-${c.key}`}
    >
      <div className={`h-8 ${c.bgColor} ${c.status === DiagnosticErrorStatus.NotComplete ? 'progress-move' : ''}`}></div>
      <span className={`absolute inset-0 flex items-center px-5 ${c.text} text-xs font-medium`}>
        {c.icon && <img className={`w-6 h-6 mr-2 ${c.svg}`} src={c.icon} alt={c.name} />}
        <span>{c.name}</span>
      </span>
    </div>
  );

  const renderDeviceSelect = useCallback(
    ({ field }: { field: ControllerRenderProps<FormValues, 'device'> }) => (
      <SingleSelect
        field={field}
        optionList={devicesOptions}
        isDisabled={progress === 'started'}
        defaultValue={getValues('device')}
        sigleSelectClass="w-full min-w-max"
        placeholder={t('diagnostic.selectDeviceOptionPlaceHolder')}
        noOptionsMessage={t('component.noOptions')}
        hasFormStateError={!!errors.device}
      />
    ),
    [devicesOptions, progress, getValues, t, errors]
  );

  return (
    <>
      <div className="card dianostic-dialog min-h-[500px]" data-testid={`diagnostic-dialog-for-type-${diagnosticType}`}>
        <div className="card-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col">
              <div className="flex justify-start items-center gap-2 py-2">
                {diagnosticType === DiagnosticType.SYSTEM ? (
                  <label className="text-sm">{t('diagnostic.system.description')}</label>
                ) : (
                  <div className="flex items-center space-x-1 w-full">
                    <label className={`text-sm ${currentLng === 'jp' ? 'w-48' : 'w-28'}`}>{t('diagnostic.selectDevice')}:</label>
                    <Controller control={control} name="device" rules={{ required: Validator().required }} render={renderDeviceSelect} />
                  </div>
                )}
                {['init', 'finished'].includes(progress) && (
                  <button type="submit" className="btn btn-primary" data-testid={`diagnostic-dialog-diagnose-btn-for-type-${diagnosticType}`}>
                    {t('modal.btnStart')}
                  </button>
                )}
              </div>
              {errors.device && (
                <div className="flex items-center w-full">
                  <label className={`text-sm ${currentLng === 'jp' ? 'w-48' : 'w-28'}`}></label>
                  <span className="w-full span-error px-1">{errors.device.message}</span>
                </div>
              )}
            </div>

            {progress === 'started' && (
              <div className="space-y-3">
                <span className="text-xs ml-4 opacity-60">{t('diagnostic.waitForDiagnosing')}</span>
                {checks.map((c) =>
                  c.status !== DiagnosticErrorStatus.NotComplete ? <Report c={c} key={c.key} /> : <CheckWithProgress c={c} key={c.key} />
                )}
              </div>
            )}
            {progress === 'finished' && (
              <>
                {completedChecks.length > 0 && (
                  <div className="space-y-3">
                    <span className="text-xs ml-4 opacity-60">{t('diagnostic.diagnosisCompleted')}</span>
                    {completedChecks.map((c) => (
                      <Report c={c} key={c.key} />
                    ))}
                  </div>
                )}
                {incompleteChecks.length > 0 && (
                  <div className="space-y-3 mt-6">
                    <span className="text-xs ml-4 opacity-60">{t('diagnostic.diagnosisIncomplete')}</span>
                    {incompleteChecks.map((c) => (
                      <Report c={c} key={c.key} />
                    ))}
                  </div>
                )}
              </>
            )}
            <div className="card-actions mt-4 justify-end">
              {progress === 'started' && (
                <button
                  type="button"
                  className={`btn btn-outline ${cancelDiagnose ? 'loading' : ''}`}
                  onClick={onCancelDiagnose}
                  data-testid={`diagnostic-dialog-cancel-btn-for-type-${diagnosticType}`}
                >
                  {t('modal.btnCancel')}
                </button>
              )}
              {progress === 'finished' && (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={onCloseDiagnosticDialog}
                  data-testid={`diagnostic-dialog-close-btn-for-type-${diagnosticType}`}
                >
                  {t('modal.btnClose')}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
});

export default DiagnosticDialog;
